import React, { useState } from 'react';
import { X } from 'lucide-react';
import type { Role } from '../../types';
import { PrioritySelector } from '../Tasks/PrioritySelector';
import { LabelSelector } from '../Tasks/LabelSelector';
import type { Priority, TaskLabel } from '../../types';

interface QuickAddProps {
  initialTask?: Task;
  onAdd: (task: Omit<Task, 'id' | 'status'>) => void;
  onClose: () => void;
  onDelete?: (taskId: string) => void;
}

export function QuickAdd({ initialTask, onAdd, onClose, onDelete }: QuickAddProps) {
  const [title, setTitle] = useState(initialTask?.title || '');
  const [description, setDescription] = useState(initialTask?.description || '');
  const [role, setRole] = useState<Role>(initialTask?.role || 'work');
  const [priority, setPriority] = useState<Priority>(initialTask?.priority || 'medium');
  const [selectedLabels, setSelectedLabels] = useState<TaskLabel[]>([]);

  const handleDelete = () => {
    if (initialTask && onDelete && confirm('Are you sure you want to delete this task?')) {
      onDelete(initialTask.id);
    }
  };

  React.useEffect(() => {
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (title.trim()) {
      // Get selected time from localStorage if it exists
      const selectedTime = localStorage.getItem('selectedTaskTime');
      const dueDate = selectedTime ? new Date(selectedTime) : new Date();
      
      if (!selectedTime) {
        // If no selected time, set to next hour
        const now = new Date();
        dueDate.setHours(now.getHours() + 1, 0, 0, 0);
      }

      const taskData = {
        title: title.trim(),
        description: description.trim(),
        role,
        priority, 
        duedate: dueDate.toISOString()
      };
      onAdd(taskData);
      localStorage.removeItem('selectedTaskTime'); // Clean up
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center">
      <div className="bg-[#2A2B2E] rounded-3xl p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">{initialTask ? 'Edit Task' : 'Add New Task'}</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/20 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs to be done?"
            className="w-full px-4 py-3 bg-black/20 rounded-2xl mb-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]"
            autoFocus
          />

          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add a brief description (optional)"
            className="w-full px-4 py-3 bg-black/20 rounded-2xl mb-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] min-h-[80px] resize-none"
          />

          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="w-full px-4 py-3 bg-black/20 rounded-2xl mb-4 text-white focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] [&>option]:bg-[#2A2B2E]"
          >
            <option value="work">Work</option>
            <option value="family">Family</option>
            <option value="personal">Personal</option>
          </select>

          <div className="mb-4">
            <PrioritySelector value={priority} onChange={setPriority} />
          </div>

          <div className="mb-4">
            <LabelSelector
              selectedLabels={selectedLabels}
              onLabelsChange={setSelectedLabels}
            />
          </div>

          <div className="space-y-3">
            <button
              type="submit"
              className="w-full bg-[var(--accent-color)] text-black rounded-xl py-3 font-medium hover:opacity-90 transition-opacity mb-3"
            >
              {initialTask ? 'Update Task' : 'Add Task'}
            </button>
            {initialTask && onDelete && (
              <button
                type="button"
                onClick={handleDelete}
                className="w-full bg-red-500/10 text-red-500 rounded-xl py-3 font-medium hover:bg-red-500/20 transition-colors"
              >
                Delete Task
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}